/**
 * Curated podcast-style narration scripts — Admin panel helps.
 * Keyed by ListenButton guideId. See index.js for the writing style guide.
 */

export const ADMIN_NARRATIONS = {
  'project-management-help': [
    {
      title: 'Where a job comes from',
      icon: '🏗️',
      turns: [
        {
          speaker: 'clara',
          speech: 'Welcome to Project Management, the list of every job the portal knows about. Almost every job here was born on the Estimates page, the moment someone pressed Award on a bid. That click copies the estimate name, the client, the division, and the disciplines onto a brand new job, and links the two together.',
        },
        {
          speaker: 'liam',
          speech: 'And that link is the thing to understand. The job knows which estimate it came from, and the estimate knows which job it became. Keep both sides agreeing and every report, resource plan, and M T O push lines up. Let one side drift and you get a bid that says awarded while the job says it belongs to a different bid.',
        },
      ],
    },
    {
      title: 'What Manage can and cannot touch',
      icon: '🔒',
      turns: [
        {
          speaker: 'clara',
          speech: 'Manage opens the job. You can rename it, set the client and division, pick the project type and disciplines, decide who is on the job and what role they carry there, and tune the reminder e-mails. Two fields are shown but locked for everyone: the job number and the estimate number.',
        },
        {
          speaker: 'liam',
          speech: 'The job number is locked because it is the key in trackers, time, M T O, and nearly every other table in the portal. The estimate number is locked because typing a new one by hand would move the job but leave the old bid still marked awarded. That is exactly the mess we are trying to avoid.',
        },
      ],
    },
    {
      title: 'Wrong bid, right job number',
      icon: '🩹',
      turns: [
        {
          speaker: 'clara',
          speech: 'Here is the most common mistake. A client has three bids with names that look alike, and the award lands on the neighbour. The job number is fine. The people on the job are fine. Only the bid is wrong.',
        },
        {
          speaker: 'liam',
          speech: 'If you are a superadmin, open the job, go to Details, and scroll to the red box called Superadmin corrections. Press Change linked estimate. Search for the right bid, dashes or no dashes, pick it, type its number once more, and press Move the award.',
        },
        {
          speaker: 'clara',
          speech: 'The job stays. Everyone on the job stays, and they are given the new estimate too so they can see the bid. The old estimate goes back to not awarded, the new one is marked awarded to this job, and the audit log records who did it and from what to what.',
        },
      ],
    },
    {
      title: 'Wrong job number',
      icon: '⚠️',
      turns: [
        {
          speaker: 'liam',
          speech: 'Changing the number itself is the dangerous one, so it is allowed only while the job is still empty. Press Change job number and the portal first counts every table that holds this job.',
        },
        {
          speaker: 'clara',
          speech: 'If all it finds is the job itself, its estimate link, its people, and its disciplines, you type the new number twice and it goes through, people included. If it finds tracker rows, time, M T O, anything else at all, it refuses and lists the tables. That clean-up stays with someone who can work in the database.',
        },
      ],
    },
    {
      title: 'Who sees what',
      icon: '👀',
      turns: [
        {
          speaker: 'clara',
          speech: 'Admins see every job and can Manage. Only superadmins see the corrections box, and only superadmins can archive, restore, or delete a job.',
        },
        {
          speaker: 'liam',
          speech: 'And every correction writes a line to the Audit Log tab with the old and new values, so nothing is changed quietly. Fix the link, check the log, move on.',
        },
      ],
    },
  ],

  'admin-notifications-help': [
    {
      title: 'What this page is for',
      icon: '📬',
      turns: [
        {
          speaker: 'clara',
          speech: 'Welcome to Admin Notifications — the control room for automated portal emails. If you have ever wondered who gets the Monday resource report, or how someone stops getting a digest, this is the place.',
        },
        {
          speaker: 'liam',
          speech: 'Recipients answers who and when. Email Log answers what actually went out. And every person still keeps a personal mute switch under Profile, Notification Settings.',
        },
      ],
    },
    {
      title: 'Managed list versus auto audience',
      icon: '🏷️',
      turns: [
        {
          speaker: 'liam',
          speech: 'There are two big modes. Managed list means you pick the people. The count says how many are on the list. Resource Plan works that way. It replaced the separate allocation report and the field plan reminder.',
        },
        {
          speaker: 'clara',
          speech: 'Auto audience means the job already knows who to email — project assignees, Technical Coordinators, division managers. Hold Digest is the classic example. When the row says picked at send time, that is healthy, not empty.',
        },
        {
          speaker: 'liam',
          speech: 'Required is the third mode — welcome emails, ticket replies, mentions. Those must reach the person involved, so there is no Profile toggle.',
        },
      ],
    },
    {
      title: 'Why auto audience looks empty',
      icon: '🧭',
      turns: [
        {
          speaker: 'clara',
          speech: 'This page does not try to list every project assignee for every digest. That list changes every week. Instead it shows who muted the email in their Profile.',
        },
        {
          speaker: 'liam',
          speech: 'So if Hold Digest shows picked at send time with zero mutes, the real recipients are still the people on those projects. Open Email Log after Friday if you want the exact To line.',
        },
      ],
    },
    {
      title: 'How you change who gets mail',
      icon: '✏️',
      turns: [
        {
          speaker: 'clara',
          speech: 'For a managed list, expand the row or use Add or edit a user and flip the toggle on. They can still mute later from Profile.',
        },
        {
          speaker: 'liam',
          speech: 'For auto audience, change who is assigned on the project, or clear a Profile mute if someone stopped getting mail.',
        },
      ],
    },
    {
      title: 'Send it now',
      icon: '📤',
      turns: [
        {
          speaker: 'clara',
          speech: 'Every row has a Send now button. Pick the people, add a copy line if you want one, and the portal sends that email right away instead of waiting for the schedule.',
        },
        {
          speaker: 'liam',
          speech: 'On a managed list, start from the people already on it, then add or drop names. You can type a shared mailbox, like Project Controls, even though it is not a portal user.',
        },
        {
          speaker: 'clara',
          speech: 'Some emails are built per person, like the hold digest or the Friday schedule request. Send now gives each chosen person their own copy. If they have nothing due, they are skipped and the screen says so.',
        },
        {
          speaker: 'liam',
          speech: 'A few rows cannot be sent from here: a sign-in code, a ticket reply, the disk alert, and anything that only exists after an upload. The button tells you why. After a send, Email Log shows the real To line.',
        },
      ],
    },
    {
      title: 'The Friday schedule updates tab',
      icon: '📅',
      turns: [
        {
          speaker: 'clara',
          speech: 'The third tab is different. It is one row per job that has a schedule, and the box at the left is the Friday seven A M progress update request for that job. Tick it on, tick it off. It is the same switch you find under the job\'s Schedule page, behind the Update button.',
        },
        {
          speaker: 'liam',
          speech: 'And the audience column tells you where the names come from. P six Upd Resp means the portal reads the responsible person straight from the schedule sync. My list means you typed the people yourself, each with their own slice of the schedule, a W B S node, a trade, or the whole thing. Both does both.',
        },
        {
          speaker: 'clara',
          speech: 'If a job is on but nobody would actually get the e-mail, the row says so in red. Press Set up to fix it, add people, add an extra C C, and send yourself a test before Friday comes around.',
        },
        {
          speaker: 'liam',
          speech: 'Each person receives two P D Fs, the three week look ahead with boxes to write in, and the full schedule, both filtered to their slice. The project manager and Project Controls ride along in copy. Happy notifying!',
        },
      ],
    },
  ],

  'manage-tags-help': [
    {
      title: 'Why tags exist',
      icon: '🏷️',
      turns: [
        {
          speaker: 'clara',
          speech: 'Welcome to tags — the little colored labels you can stick on almost anything in the portal. A matrix line, a tracker row, and over time, records in every module.',
        },
        {
          speaker: 'liam',
          speech: 'And here is the why. Columns and statuses are great for the data everyone shares, but teams always need one more layer — flag this line as rework, mark these ten items for the client walk-down, group a punch list. Tags give you that layer without touching the database schema every time.',
        },
        {
          speaker: 'clara',
          speech: 'Exactly. Think of them as sticky notes that the whole team can see, search, and filter by.',
        },
      ],
    },
    {
      title: 'Categories keep it tidy',
      icon: '🗂️',
      turns: [
        {
          speaker: 'liam',
          speech: 'Every tag lives inside a category, and categories are where admins set the rules. Each one has a default color, so tags stay visually consistent.',
        },
        {
          speaker: 'clara',
          speech: 'The first rule is scope. A global category shares one tag list across the entire portal — perfect for labels like rework or on hold that mean the same thing everywhere. A per-project category gives every job its own private list, so job labels never bleed between projects.',
        },
        {
          speaker: 'liam',
          speech: 'The second rule is who can add tags. If a category is admin curated, the list is locked — people can apply the tags but not invent new ones. If you allow user creation, anyone can type a new tag right in the picker and keep working.',
        },
        {
          speaker: 'clara',
          speech: 'And the third rule is where the category shows up. By default it applies everywhere, but you can limit it to just the piping matrix, just the trackers, or any mix.',
        },
      ],
    },
    {
      title: 'Keeping the garden weeded',
      icon: '🧹',
      turns: [
        {
          speaker: 'clara',
          speech: 'Free-form tagging drifts over time — someone types re-work with a dash, someone else types rework without one. That is what merge is for. Pick the duplicate, choose the tag it should become, and every assignment moves over before the duplicate disappears.',
        },
        {
          speaker: 'liam',
          speech: 'Retire is the gentler option. A retired tag vanishes from the pickers but stays on the records that already carry it, so history is preserved. Delete is the nuclear one — it removes the tag and pulls it off every record. The usage counts next to each tag tell you how big that blast radius is before you click.',
        },
        {
          speaker: 'clara',
          speech: 'Every change here — creates, renames, merges, deletes — is written to the audit log, one click away in the header.',
        },
      ],
    },
    {
      title: 'Where you will meet tags day to day',
      icon: '📌',
      turns: [
        {
          speaker: 'liam',
          speech: 'Out in the portal, tags show up as a column on the progress trackers. Click a cell to open the picker, search, apply, or create. Select a bunch of rows and you can tag them all at once from the toolbar — that is the move for thousand-row matrices.',
        },
        {
          speaker: 'clara',
          speech: 'And because tags are filterable like any other column, the payoff comes later: filter to everything marked needs review, clear them one by one, and enjoy the empty list. Happy tagging!',
        },
      ],
    },
  ],

  'letterhead-settings-help': [
    {
      title: 'One header, every download',
      icon: '📄',
      turns: [
        {
          speaker: 'clara',
          speech: 'Welcome to letterhead. This is the official company band that now sits on every portal P D F — logo, legal name, address, phone, then the job line and the project type.',
        },
        {
          speaker: 'liam',
          speech: 'A division here is a legal entity. Ltd Canada, L G L Inc, Tanks, Quebec. Not a trade. Each one gets its own footer copied from the Accounting Word files, so we are not inventing addresses.',
        },
      ],
    },
    {
      title: 'How a job picks a company',
      icon: '🔢',
      turns: [
        {
          speaker: 'liam',
          speech: 'T J’s rule: the job number prefix picks the company. Sixty one is L G L Inc. Seventy one is Laf Group Ltd Canada. The table is editable, and every seeded row starts unconfirmed until he signs off.',
        },
        {
          speaker: 'clara',
          speech: 'Resolution order is prefix first, then the project’s division, then the generic Canada letterhead. If a job has no project type yet, the second line is three dots. Existing jobs stay blank until someone edits them.',
        },
      ],
    },
    {
      title: 'Temp jobs and Florida',
      icon: '🗺️',
      turns: [
        {
          speaker: 'liam',
          speech: 'A leading T is a temp or time and materials job. We try the raw number first, so T sixty three can be its own rule, then we strip the T so T sixty one zero seven five still follows sixty one.',
        },
        {
          speaker: 'clara',
          speech: 'Prefix thirty uses Syracuse only Ltd U S. Prefix sixty one uses the L G L Inc letterhead with Bartow and Syracuse. The Accounting file called Ltd U S with Florida is not used unless T J assigns a prefix to it.',
        },
      ],
    },
    {
      title: 'Questions for T J',
      icon: '❓',
      turns: [
        {
          speaker: 'clara',
          speech: 'We still need him to confirm series seventy five, sixty three, T sixty three, and any others like twenty, fifty, eighty, or Quebec. Also: which job numbers belong to M R L?',
        },
        {
          speaker: 'liam',
          speech: 'And the official project type list — Lump Sum, time and materials, Unit Price, Cost Plus — plus whether a SharePoint job site title should differ from job, discipline, name. Paste answers later and flip Confirmed on each prefix row. Happy branding!',
        },
      ],
    },
  ],

  'nomenclature-settings-help': [
    {
      title: 'Why names were a mess',
      icon: '🔤',
      turns: [
        {
          speaker: 'clara',
          speech: 'Welcome to nomenclature. Every page used to invent its own download name. Forecasting, change orders, N D E, timesheets — same company, five different filename styles.',
        },
        {
          speaker: 'liam',
          speech: 'Now there is one pattern: job, report slug, date, extension. Example: sixty one zero seven five, Forecasting Analytics, twenty twenty six dash zero eight dash seventeen, P D F.',
        },
      ],
    },
    {
      title: 'Job line and project type',
      icon: '📋',
      turns: [
        {
          speaker: 'liam',
          speech: 'The letterhead job line is job, client, job name when a client is set. No client, just job and job name. The line under that is the project type.',
        },
        {
          speaker: 'clara',
          speech: 'If type is blank, we print three dots. New jobs require a type on create. Old jobs stay blank until someone fills them in on Project Details. The allowed list lives on this page — add or remove and the dropdowns follow.',
        },
      ],
    },
    {
      title: 'SharePoint stays put',
      icon: '📁',
      turns: [
        {
          speaker: 'clara',
          speech: 'Estimate folders on SharePoint already follow year, region, sequence — L G, U S, F L, L I, Q C. Costing job sites are job, discipline, name. We document those here so new portal downloads match the office.',
        },
        {
          speaker: 'liam',
          speech: 'We do not rename existing SharePoint files or site titles. That would be chaos. This page is the standard going forward, not a retrofit.',
        },
      ],
    },
    {
      title: 'P six resources',
      icon: '👷',
      turns: [
        {
          speaker: 'clara',
          speech: 'New Primavera resources follow one pattern. The I D is estimate key, dash, trade token, then optional dash F A B, then optional dash E X. The name is the trade words, plus Fabrication when it is shop, plus Extra or Additional Scope when it is extra.',
        },
        {
          speaker: 'liam',
          speech: 'Fab versus Field is the name, not a P six column. Extra is an E X token on the I D, or Extra or Additional Scope in the name. The Discipline U D F should use the same words as the name. We document the pattern here. We do not rename the old resources.',
        },
      ],
    },
    {
      title: 'Questions for T J',
      icon: '❓',
      turns: [
        {
          speaker: 'liam',
          speech: 'Confirm the project type list. Confirm the job site title if it should differ. And tell us if the office already uses filename tokens we should add.',
        },
        {
          speaker: 'clara',
          speech: 'Once those answers land, we update the patterns here — no rebuild of the design. Happy naming!',
        },
      ],
    },
  ],
  'maestro-actuals-import-help': [
    {
      title: 'One file, every job',
      icon: '📥',
      turns: [
        {
          speaker: 'clara',
          speech: 'Remember uploading Maestro hours one project at a time? This page retires that. Export every project from Maestro in one go, drop the file here, and the portal splits it by job number and loads the spent hours for each project it knows.',
        },
        {
          speaker: 'liam',
          speech: 'It is forgiving about names. Maestro codes every discipline separately, like seven one nine two three S T R. The portal project might be called the same thing, or just seven one nine two three. Both land on the same project. Jobs the portal has never heard of are listed at the bottom, and nothing is written for them until someone creates the project.',
        },
      ],
    },
    {
      title: 'Only the weeks in the file',
      icon: '🪟',
      turns: [
        {
          speaker: 'liam',
          speech: 'Here is the part that matters. A Maestro export is a date window, not the whole life of a job. So for each job we only replace the weeks between its first and last week in the file. Older history stays exactly as it was.',
        },
        {
          speaker: 'clara',
          speech: 'That is what makes a rolling export safe to re-import every week. If you really want to wipe a job and rebuild it from scratch, that still lives on the project itself — the Upload Maestro button on its E V M report does a full replace.',
        },
      ],
    },
    {
      title: 'Preview, then decide',
      icon: '🔍',
      turns: [
        {
          speaker: 'clara',
          speech: 'Preview reads the file and writes nothing. For every job you see the hours in the file next to what was already seeded for those same weeks, and the difference. A red difference means the file has fewer hours than before — worth a look before you press Import.',
        },
        {
          speaker: 'liam',
          speech: 'One thing this page will never do is flip a project\'s spent source. The E V M report only reads Maestro hours when the project is set to Maestro dump. Projects still on auto that now have Maestro hours get a small Use Maestro for E V M button. Click it when you are ready, project by project.',
        },
      ],
    },
    {
      title: 'The SharePoint folder does it for you',
      icon: '📂',
      turns: [
        {
          speaker: 'liam',
          speech: 'You do not even need this page for the routine run. Drop the export in the P M O site, Portal library, Resource Histogram, Maestro Actuals folder. Every night at three the portal picks up any file it has not seen, imports it the same way, and e-mails the reconciliation to the Maestro Actuals Import Summary roster under Admin Notifications.',
        },
        {
          speaker: 'clara',
          speech: 'A file is imported once. Save a new version of it and it goes again. Check folder now runs the pickup immediately, and Recent runs keeps every summary so you can always see what changed and when. That is the whole loop.',
        },
      ],
    },
  ],
};
